/* eslint-disable react/no-array-index-key */
import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useMediaQuery } from '@mui/material';
import actions from '@src/redux/actions';
import theme from '@src/styles/theme';
import SmallLockCharacterBanner from '../CustomHeaderBanner/SmallLockCharacterBanner';
import HeaderBanner from '../HeaderBanner';
import { StyledSwiperHeaderBanner } from './index.style';
import OtherVoucherBanner from '../CustomHeaderBanner/OtherVoucherBanner';
import NewReferralBanner from '../NewReferralBanner';
import TopPaidEventHeaderBanner from '../TopPaidEventHeaderBanner';

// Horizontal distance a touch has to travel to count as a swipe, not a tap.
const SWIPE_THRESHOLD = 40;

const SwiperHeaderBanner = ({ headerVouchers, isAgreeToTerm }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  // Bumped on every swipe so the 5s autoplay restarts from the slide the user picked.
  const [autoplayKey, setAutoplayKey] = useState(0);
  const touchStartXRef = useRef(null);
  const hasSwipedRef = useRef(false);
  const total = headerVouchers.length;
  const dispatch = useDispatch();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const openSidebar = useSelector((state) => state.layout.openSidebar);

  useEffect(() => {
    setCurrentSlide(0);
  }, [total]);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prevSlide) =>
        prevSlide === total - 1 ? 0 : prevSlide + 1,
      );
    }, 5000);

    return () => clearInterval(interval);
  }, [total, autoplayKey]);

  const handleTouchStart = (event) => {
    touchStartXRef.current = event.touches[0].clientX;
    hasSwipedRef.current = false;
  };

  const handleTouchEnd = (event) => {
    if (touchStartXRef.current === null || total < 2) return;
    const deltaX = event.changedTouches[0].clientX - touchStartXRef.current;
    touchStartXRef.current = null;
    if (Math.abs(deltaX) < SWIPE_THRESHOLD) return;

    hasSwipedRef.current = true;
    const step = deltaX < 0 ? 1 : -1;
    setCurrentSlide((prevSlide) => (prevSlide + step + total) % total);
    setAutoplayKey((key) => key + 1);
  };

  // A swipe ends with a click on the banner under the finger; drop it so the swipe does not open the banner.
  const handleClickCapture = (event) => {
    if (hasSwipedRef.current) {
      hasSwipedRef.current = false;
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    // An open mobile menu would hide the page the banner opens.
    if (isMobile && openSidebar)
      dispatch(actions.layout.handleChangeOpenSidebar(false));
  };

  const getSlideClass = (index) => {
    if (index === currentSlide) return 'active';
    if (
      index ===
      (currentSlide - 1 + headerVouchers.length) % headerVouchers.length
    )
      return 'prev';
    return 'next';
  };

  return (
    <StyledSwiperHeaderBanner
      isAgreeToTerm={isAgreeToTerm}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onClickCapture={handleClickCapture}
    >
      {headerVouchers &&
        headerVouchers.length > 0 &&
        headerVouchers.map((voucher, index) => {
          const slideClass = getSlideClass(index);

          const isFlashSaleVoucher = voucher?.isHeaderBanner;
          const isOnboardingTasksBanner = voucher?.isOnboardingTasksBanner;

          if (voucher?.isTopPaidEventBanner) {
            return (
              <div key="top-paid-event" className={slideClass}>
                <TopPaidEventHeaderBanner />
              </div>
            );
          }

          if (isOnboardingTasksBanner) {
            return (
              <div
                key={voucher.id || `onboarding-tasks-${index}`}
                className={slideClass}
              >
                <NewReferralBanner />
              </div>
            );
          }

          if (isFlashSaleVoucher) {
            return (
              <div
                key={voucher.id || `flash-sale-${index}`}
                className={slideClass}
              >
                <HeaderBanner
                  headerVoucher={voucher}
                  isAgreeToTerm={isAgreeToTerm}
                />
              </div>
            );
          }

          if (voucher.isPreserveVoucher) {
            return (
              <div key={`preserve-${index}`} className={slideClass}>
                <SmallLockCharacterBanner />
              </div>
            );
          }

          return (
            <div key={voucher.id || `other-${index}`} className={slideClass}>
              <OtherVoucherBanner voucher={voucher} />
            </div>
          );
        })}
      {total > 1 && (
        <ul className="swiper-dots" aria-hidden="true">
          {headerVouchers.map((voucher, index) => (
            <li
              key={voucher.id || index}
              className={index === currentSlide ? 'dot active' : 'dot'}
            />
          ))}
        </ul>
      )}
    </StyledSwiperHeaderBanner>
  );
};

export default SwiperHeaderBanner;
